import { Component, OnInit, OnDestroy, inject, signal, effect, untracked, computed, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  ComparisonType,
  DatePickerComponent,
  DateRangePickerOutput,
  DateRangePickerValue
} from '../../../../shared/components/date-picker/date-picker.component';
import { DropdownComponent } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { MultiStackedBarChartComponent } from '../../../../shared/components/charts/bar-charts/multi-stacked-bar-chart/multi-stacked-bar-chart.component';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import {
  getVisionboardCurrencyInfo,
  VISIONBOARD_CURRENCY_OPTIONS
} from '../../visionboard/shared/visionboard-currency.config';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { FmDashboardOverviewComponent } from '../fm-dashboard-overview/fm-dashboard-overview.component';
dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

@Component({
  selector: 'app-dashboard-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiStackedBarChartComponent,
    HorizontalBarChartComponent,
    DatePickerComponent,
    DropdownComponent,
    IconComponentV2,
    IndividualToasterComponent,
    PaginationComponent,
    FmDashboardOverviewComponent
  ],
  templateUrl: './dashboard-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./dashboard-overview.component.scss']
})
export class DashboardOverviewComponent implements OnInit, OnDestroy {
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private unitService = inject(UnitSuggestionService);
  private loaderService = inject(LoaderService);
  private fetchSubscription?: Subscription;
  /** Avoid fetching with component defaults before the date picker emits its range. */
  private datesReady = false;
  private fetchRequestId = 0;
  private lastDateRangeKey = '';
  /** Skip one effect-driven fetch when onDateRangeChange already triggered it. */
  private skipNextEffectFetch = false;
  private initialLoadDone = false;

  // Date State — synced from date picker (same as monthly-report-1)
  start_date = '';
  end_date = '';
  prev_start_date = '';
  prev_end_date = '';
  comparisonType: ComparisonType = 'none';
  unit = signal<string>('day');

  currencyOptions = VISIONBOARD_CURRENCY_OPTIONS;
  selectedCurrency = signal<string>('1');
  selectedCurrencyInfo = computed(() => getVisionboardCurrencyInfo(this.selectedCurrency()));

  get hasComparison(): boolean {
    return this.comparisonType !== 'none';
  }

  get comparisonLabel(): string {
    switch (this.comparisonType) {
      case 'previous-period': return 'Prev';
      case 'previous-month': return 'MoM';
      case 'previous-year': return 'YoY';
      case 'custom': return 'Custom';
      default: return 'MoM';
    }
  }

  productConfig = computed(() => {
    const productId = this.apiService.selectedProductId();
    if (!productId) return null;
    return this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
  });

  // Loading State
  isLoading = signal(true);

  // Data signals matching HTML
  verticalRisk: any = {};
  growthLever: any = {};
  profitProtection: any = {};
  kpis: any = {
    totalFinanced: {},
    revenueCollected: {},
    activeBusinesses: {},
    totalContracts: {},
    activationRate: {},
    topIndustryShare: {}
  };
  topMarkets: any = {
    chartSubtitle: '',
    chartFootnote: '',
    chartData: [],
    emerging: { marketCount: 0, share: 0, rows: [] }
  };
  drivingNumbers: any = {
    bestIndustry: null,
    worstIndustry: null,
    contractMixSubtitle: ''
  };

  // Charts
  contractTypeMixChart: any = null;
  activeBizChart: any = null;
  waterfallChart: any = null;
  allowedGroupByUnits: any[] = ['day', 'week', 'month', 'quarter', 'year'];

  // Critical Business Summary & Details
  isCriticalBusinessLoading = signal(false);
  criticalBusinessData = signal<any>(null);
  isRiskModalOpen = signal(false);
  riskSearchQuery = signal<string>('');
  selectedRiskTier = signal<string>('All');

  filteredRiskDetails = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details) return [];

    let list = data.details as any[];

    // Filter by search query
    const query = this.riskSearchQuery().toLowerCase().trim();
    if (query) {
      list = list.filter(item =>
        (item.practice_name || '').toLowerCase().includes(query) ||
        (item.business_owner || '').toLowerCase().includes(query)
      );
    }

    // Filter by active tier tab
    const tier = this.selectedRiskTier();
    if (tier !== 'All') {
      list = list.filter(item =>
        (item.churn_risk || '').toLowerCase() === tier.toLowerCase()
      );
    }

    return list;
  });

  riskCurrentPage = signal<number>(1);
  riskPageSize = signal<number>(10);

  riskTotalPages = computed(() => {
    const total = this.filteredRiskDetails().length;
    return Math.max(1, Math.ceil(total / this.riskPageSize()));
  });

  paginatedRiskDetails = computed(() => {
    const list = this.filteredRiskDetails();
    const page = this.riskCurrentPage();
    const size = this.riskPageSize();
    const start = (page - 1) * size;
    return list.slice(start, start + size);
  });

  paginationText = computed(() => {
    const total = this.filteredRiskDetails().length;
    const page = this.riskCurrentPage();
    const size = this.riskPageSize();
    const start = total === 0 ? 0 : (page - 1) * size + 1;
    const end = Math.min(page * size, total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  totalActiveContracts = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details) return 0;
    return data.details.reduce((sum: number, item: any) => sum + (Number(item.active_contracts) || 0), 0);
  });

  totalContractsCount = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details) return 0;
    return data.details.reduce((sum: number, item: any) => sum + (Number(item.total_contracts) || 0), 0);
  });

  atRiskBusinessesCount = computed(() => {
    const data = this.criticalBusinessData();
    if (!data) return 0;
    return (Number(data.high_risk) || 0) + (Number(data.critical_risk) || 0);
  });

  avgDaysSinceContact = computed(() => {
    const data = this.criticalBusinessData();
    if (!data || !data.details || data.details.length === 0) return 0;
    const totalDays = data.details.reduce((sum: number, item: any) => sum + (Number(item.days_since_last_contract) || 0), 0);
    return Math.round(totalDays / data.details.length);
  });

  constructor() {
    effect(() => {
      const pid = this.apiService.selectedProductId();
      if (pid && this.datesReady) {
        untracked(() => {
          if (this.skipNextEffectFetch) {
            this.skipNextEffectFetch = false;
            return;
          }
          this.fetchData();
        });
      }
    });
  }

  ngOnInit() {
    // Initial fetch is driven by date picker `dateRangeChange` (same as monthly-report-1)
  }

  ngOnDestroy() {
    this.fetchSubscription?.unsubscribe();
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    const rangeKey = [
      event.start_date,
      event.end_date,
      event.comparison_type,
      event.previous_start_date ?? '',
      event.previous_end_date ?? '',
      event.unit
    ].join('|');

    if (rangeKey === this.lastDateRangeKey) return;
    this.lastDateRangeKey = rangeKey;

    this.start_date = this.normalizeDate(event.start_date);
    this.end_date = this.normalizeDate(event.end_date);
    this.comparisonType = event.comparison_type || 'none';

    if (this.comparisonType !== 'none' && event.previous_start_date && event.previous_end_date) {
      this.prev_start_date = this.normalizeDate(event.previous_start_date);
      this.prev_end_date = this.normalizeDate(event.previous_end_date);
    } else {
      this.prev_start_date = '';
      this.prev_end_date = '';
    }

    if (event.preset_label === 'Today' || event.preset_label === 'Yesterday' || this.start_date === this.end_date) {
      this.allowedGroupByUnits = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
    } else {
      this.allowedGroupByUnits = ['day', 'week', 'month', 'quarter', 'year'];
    }

    this.unit.set(event.unit || this.unitService.getUnitSuggestions(this.start_date, this.end_date).recommended as any);
    this.datesReady = true;
    this.skipNextEffectFetch = true;
    this.fetchData();
  }

  rangeValues(_event: DateRangePickerValue) {
    // Kept for parity with monthly-report-1 date picker wiring
  }

  onCurrencyChange(value: string) {
    if (value === this.selectedCurrency()) return;
    this.selectedCurrency.set(value);
    if (this.datesReady) {
      this.fetchData();
    }
  }

  private normalizeDate(date: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return dayjs.tz(date, LA_TIMEZONE).format('YYYY-MM-DD');
    }
    return dayjs(date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  private getPayloadExtras(): Record<string, any> {
    const productId = this.apiService.selectedProductId();
    if (!productId) return {};
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    return productConfig.payloadExtras ?? {};
  }

  private buildPayload(base: Record<string, any>): Record<string, any> {
    const payload: Record<string, any> = {
      ...base,
      ...this.getPayloadExtras()
    };

    if (this.productConfig()?.productServerName === 'credee') {
      const { cocu_id, country_id } = this.selectedCurrencyInfo();
      payload['cocu_id'] = cocu_id;
      payload['country_id'] = country_id;
    }

    return payload;
  }

  private makeApiCall(endpoint: string, payload: any, isStats: boolean = false) {
    const productId = this.apiService.selectedProductId();
    if (!productId) return of(null);
    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const basePath = isStats ? 'visionboard/stats' : (productConfig.reportToolsPath || 'visionboard/tools');
    const url = `${basePath}/${endpoint}`;

    const options = {
      showLoader: false,
      showErrorToaster: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };

    return this.apiService.post(url, payload, options).pipe(
      catchError(err => {
        console.error(`Error fetching ${url}:`, err);
        return of(null);
      })
    );
  }

  private fetchData() {
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.start_date || !this.end_date) return;

    const requestId = ++this.fetchRequestId;
    this.fetchSubscription?.unsubscribe();
    this.isLoading.set(true);
    if (!this.initialLoadDone) {
      this.loaderService.show();
    }

    this.fetchCriticalBusinessData();

    const payloadCurrent = this.buildPayload({
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit(),
      overall: 0
    });

    const payloadOverall = this.buildPayload({
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit(),
      overall: 1
    });

    const includeComparison = true;
    const diffDays = dayjs(this.end_date).diff(dayjs(this.start_date), 'day') + 1;
    const effPrevStart = (this.comparisonType !== 'none' && this.prev_start_date)
      ? this.prev_start_date
      : dayjs(this.start_date).subtract(diffDays, 'day').format('YYYY-MM-DD');
    const effPrevEnd = (this.comparisonType !== 'none' && this.prev_end_date)
      ? this.prev_end_date
      : dayjs(this.end_date).subtract(diffDays, 'day').format('YYYY-MM-DD');

    const payloadPrev = this.buildPayload({
      start_time: effPrevStart,
      end_time: effPrevEnd,
      unit: this.unit(),
      overall: 0
    });

    const subIndReq = this.makeApiCall('sub_industries_contracts', { ...payloadCurrent, type: [0] }, false);
    const collEndpoints = ['upfront_collection', 'downpayment_collection', 'enrollment_collection', 'late_fee_collection'];
    const collReqs = collEndpoints.map(ep => this.makeApiCall(ep, { ...payloadCurrent }, true));
    const leakEndpoints = ['refunds', 'reversals', 'late_fee_refunded'];
    const leakReqs = leakEndpoints.map(ep => this.makeApiCall(ep, { ...payloadCurrent }, true));

    const requests: Record<string, any> = {
      subInd: subIndReq,
      bizContracts: this.makeApiCall('business_contracts_overall', payloadOverall, false),
      coll: forkJoin(collReqs),
      leak: forkJoin(leakReqs),
      rangeContracts: this.makeApiCall('range_contracts', payloadCurrent, true),
      rangeContractsOverall: this.makeApiCall('range_contracts', payloadOverall, true),
      activeBiz: this.makeApiCall('active_business', payloadCurrent, true),
      totalBiz: this.makeApiCall('total_business', payloadOverall, true),
      countryContracts: this.makeApiCall('country_contracts', payloadCurrent, false),
      contractTypes: this.makeApiCall('contract_types_count', { ...payloadCurrent, overall: 1 }, false),
      activeBizStats: this.makeApiCall('active_business_stats', payloadCurrent, false)
    };

    if (includeComparison && payloadPrev) {
      const subIndReqPrev = this.makeApiCall('sub_industries_contracts', { ...payloadPrev, type: [0] }, false);
      const collReqsPrev = collEndpoints.map(ep => this.makeApiCall(ep, { ...payloadPrev }, true));
      requests['subIndPrev'] = subIndReqPrev;
      if (this.hasComparison) {
        requests['bizContractsPrev'] = this.makeApiCall('business_contracts_overall', payloadPrev, false);
      }
      requests['collPrev'] = forkJoin(collReqsPrev);
      requests['rangeContractsPrev'] = this.makeApiCall('range_contracts', payloadPrev, true);
      requests['activeBizPrev'] = this.makeApiCall('active_business', payloadPrev, true);
    }

    this.fetchSubscription = forkJoin(requests).subscribe({
      next: (res: any) => {
        if (requestId !== this.fetchRequestId) return;
        this.processData(res, includeComparison);
        this.isLoading.set(false);
        if (!this.initialLoadDone) {
          this.loaderService.hide();
          this.initialLoadDone = true;
        }
      },
      error: (err) => {
        console.error('Failed to fetch dashboard overview data', err);
        if (requestId === this.fetchRequestId) {
          this.isLoading.set(false);
        }
        if (!this.initialLoadDone) {
          this.loaderService.hide();
          this.initialLoadDone = true;
        }
      }
    });
  }

  private formatNumber(num: number): string {
    return new Intl.NumberFormat('en-US').format(num);
  }

  /** Matches contract type mix barHeight (40) so the x-axis fits inside the card. */
  getContractTypeMixChartMinHeight(): number {
    const count = this.contractTypeMixChart?.length ?? 0;
    return count > 0 ? count * 28 : 120;
  }

  /** Adequate left margin to fit full Payment Plan names without clipping. */
  getContractTypeMixLeftMargin(): number {
    return 130;
  }

  /** X-axis ceiling rounded to nearest 5%, matching HTML reference scale. */
  getContractTypeMixMaxVal(): number {
    const chart = this.contractTypeMixChart;
    if (!chart?.length) return 40;
    const maxVal = Math.max(...chart.map((d: { value: number }) => d.value));
    return Math.ceil(maxVal / 5) * 5 || 5;
  }

  /** Top markets — tight left margin from label length (country names are short). */
  getTopMarketsLeftMargin(): number {
    const labels = this.topMarkets.chartData?.map((d: { label: string }) => d.label) ?? [];
    if (!labels.length) return 60;
    const maxChars = Math.max(...labels.map((l: string) => l.length));
    return Math.min(100, Math.max(56, maxChars * 6.5 + 16));
  }

  getTopMarketsChartMinHeight(): number {
    const count = this.topMarkets.chartData?.length ?? 0;
    return count > 0 ? count * 56 : 112;
  }

  /** Contract-count x-axis ceiling (e.g. 205 → 250). */
  getTopMarketsMaxVal(): number {
    return 100;
  }

  get formattedDateRange(): string {
    if (!this.start_date || !this.end_date) return '';
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    return `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`;
  }

  shouldEnableScroll(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    return end.diff(start, 'year') >= 6 || end.diff(start, 'day') >= 1800;
  }

  /** Waterfall — labels wrap at ~80px in the chart. */
  getWaterfallLeftMargin(): number {
    return 110;
  }

  getWaterfallChartMinHeight(): number {
    const count = this.waterfallChart?.data?.length ?? 0;
    return count > 0 ? count * 28 : 128;
  }

  /** Dollar x-axis ceiling from largest absolute inflow/outflow. */
  getWaterfallMaxVal(): number {
    const chart = this.waterfallChart?.data;
    if (!chart?.length) return 50000;
    const maxVal = Math.max(...chart.map((d: { value: number }) => Math.abs(d.value)));
    if (maxVal <= 1000) return Math.ceil(maxVal / 100) * 100 || 100;
    if (maxVal <= 10000) return Math.ceil(maxVal / 1000) * 1000;
    if (maxVal <= 100000) return Math.ceil(maxVal / 20000) * 20000;
    return Math.ceil(maxVal / 50000) * 50000;
  }

  getWaterfallTickStep(): number {
    const maxVal = this.getWaterfallMaxVal();
    let step = 10;
    if (maxVal > 200) {
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
      step = magnitude;
      if (maxVal / step < 3) step = magnitude / 2;
      if (maxVal / step > 8) step = magnitude * 2;
    }
    return step;
  }

  getWaterfallMinVal(): number {
    const chart = this.waterfallChart?.data;
    if (!chart?.length) return -50000;

    const minDataVal = Math.min(0, ...chart.map((d: { value: number }) => d.value));
    const step = this.getWaterfallTickStep();

    return Math.floor(minDataVal / step) * step;
  }

  getWaterfallTickValues(): number[] {
    const step = this.getWaterfallTickStep();
    const maxVal = this.getWaterfallMaxVal();
    const minVal = this.getWaterfallMinVal();
    const ticks: number[] = [];

    // Ticks going backwards from 0
    for (let i = 0; i >= minVal; i -= step) {
      if (!ticks.includes(i)) ticks.push(i);
    }

    // Explicitly add minVal so it shows on the axis if it's not a multiple of step
    if (minVal < 0 && !ticks.includes(minVal)) {
      ticks.push(minVal);
    }

    // Ticks going forwards from 0
    for (let i = step; i <= maxVal; i += step) {
      if (!ticks.includes(i)) ticks.push(i);
    }

    return ticks.sort((a, b) => a - b);
  }

  /** contract_types_count — supports top-level `overall` (MR1) and nested `data.overall`. */
  private getContractTypesOverall(res: any): any[] {
    if (!res) return [];
    // const overall = res.overall ?? res.data?.overall;
    // if (Array.isArray(overall) && overall.length) return overall;

    const unitData = res.unit_data ?? res.data?.unit_data;
    if (Array.isArray(unitData) && unitData.length) {
      return unitData.filter((row: any) => this.getContractTypeLabel(row) !== 'Unknown');
    }
    return [];
  }

  /** Supports both `contract_type_label` (mapping doc) and `contract_type_name` (MR1 API). */
  private getContractTypeLabel(item: any): string {
    const label = item?.contract_type_label ?? item?.contract_type_name ?? item?.label ?? 'Unknown';
    const nameMapping: Record<string, string> = {
      'No Fee': 'No Fee Payment',
      '10%': '10% Payment',
      'In-House': 'In-House Payment',
      'EZ-Di': 'EZ Deferred Payment',
      'AR': 'AR Payment',
      '0 Reserve': 'Zero Reserve Payment',
      'EZ': 'EZ Payment',
      'Cool Credit': 'Cool Credit Payment'
    };
    return nameMapping[label] || label;
  }

  /** Supports array or `{ data: [] }` from country_contracts. */
  private getCountryContractsData(res: any): any[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    return [];
  }

  private processData(res: any, includeComparison = false) {
    const getArr = (r: any) => r?.data || [];
    const getStats = (r: any) => r?.stats || {};
    const getRecordsStats = (r: any) => r?.records?.stats || {};

    const mergedSubInd = getArr(res.subInd);
    const mergedSubIndPrev = includeComparison && res.subIndPrev
      ? getArr(res.subIndPrev)
      : [];

    // Vertical Risk
    this.verticalRisk = {};
    const filteredSubIndustries = mergedSubInd.filter((e: any) => Number(e.overdue_ratio) > 0);
    if (filteredSubIndustries.length > 0) {
      const targetSubIndustry = filteredSubIndustries.reduce((a: any, b: any) =>
        Number(a.average_financed_amount) > Number(b.average_financed_amount) ? a : b
      );
      this.verticalRisk = {
        name: targetSubIndustry.speciality_name,
        overdue: targetSubIndustry.overdue_ratio,
        contracts: targetSubIndustry.total_contracts
      };
    }

    // Growth Lever
    const bizData = res.bizContracts?.data || {};
    const totalBiz = bizData.total_businesses || 0;


    // Calculate how many of the newly enrolled businesses actually got a contract using the new signup pct
    const newSignupActivated = Math.round(totalBiz * ((bizData.activation_rate_new_signup_pct || 0) / 100));
    //  const inactiveCount = Math.max(0, totalBiz - newSignupActivated);
    // const isDenefits = this.productConfigService.isDenefitsProduct(this.apiService.selectedProductId());
    // isDenefits
    // ?Math.max(0, totalBiz - newSignupActivated)
    // :
    const inactiveCount = Math.max(0, (bizData.total_businesses || 0) - (bizData.businesses_with_contracts || 0));

    this.growthLever = {
      inactive: inactiveCount,
      enrolled: totalBiz,
      activationRate: bizData.activation_rate_new_signup_pct || bizData.activation_rate_pct || 0
    };

    // KPIs (moved up to use for proxy calculations)
    const rcStats = getRecordsStats(res.rangeContracts);
    const rcStatsPrev = includeComparison ? getRecordsStats(res.rangeContractsPrev) : {};

    // Profit Protection
    const grossCollected = res.coll.reduce((sum: number, r: any) => sum + (Number(getStats(r).amount) || 0), 0);

    const refundsAmount = Number(getStats(res.leak[0]).amount || 0);
    const reversalsAmount = Number(getStats(res.leak[1]).amount || 0);
    const leakage = refundsAmount + reversalsAmount;

    const refundsCount = Number(getStats(res.leak[0]).count || 0);
    const reversalsCount = Number(getStats(res.leak[1]).count || 0);
    const leakCount = refundsCount + reversalsCount;

    const netRevenue = grossCollected - leakage;

    // Proxy calculation for same-day cancellations since timing signal is missing
    const proxyContracts = rcStats.total_contracts || 1;
    const sameDayCancelPct = Math.min(100, Math.round((leakCount / proxyContracts) * 100));

    this.profitProtection = {
      leakage: leakage,
      sameDayCancelPct: sameDayCancelPct > 0 ? sameDayCancelPct : 12
    };

    // KPIs
    const totalFinanced = rcStats.total_financed_amount || 0;
    const totalFinancedPrev = rcStatsPrev.total_financed_amount || 0;
    const momFinanced = includeComparison && totalFinancedPrev
      ? ((totalFinanced - totalFinancedPrev) / totalFinancedPrev) * 100
      : 0;
    const totalContracts = rcStats.total_contracts || 0;
    const avgPerContract = totalContracts ? totalFinanced / totalContracts : 0;

    this.kpis.totalFinanced = {
      value: totalFinanced,
      meta: `Avg per contract $${this.formatNumber(Math.round(avgPerContract))} · across ${this.formatNumber(totalContracts)} active contracts`,
      mom: momFinanced
    };

    const grossCollectedPrev = includeComparison && res.collPrev
      ? res.collPrev.reduce((sum: number, r: any) => sum + (getStats(r).amount || 0), 0)
      : 0;
    const momRevenue = includeComparison && grossCollectedPrev
      ? ((grossCollected - grossCollectedPrev) / grossCollectedPrev) * 100
      : 0;
    this.kpis.revenueCollected = {
      value: grossCollected,
      meta: `Upfront $${this.formatNumber(Math.round(getStats(res.coll[0]).amount || 0))} · Down $${this.formatNumber(Math.round(getStats(res.coll[1]).amount || 0))} · Enroll $${this.formatNumber(Math.round(getStats(res.coll[2]).amount || 0))} · Late $${this.formatNumber(Math.round(getStats(res.coll[3]).amount || 0))}`,
      mom: momRevenue
    };

    const abStats = res.activeBiz?.data?.stats || {};
    const abStatsPrev = includeComparison && res.activeBizPrev ? (res.activeBizPrev.data?.stats || {}) : {};
    const tbStats = res.totalBiz?.data?.stats || {};
    const activeBizValue = abStats.total || 0;

    const records = res.activeBiz?.data?.records || [];
    let momActiveBiz = 0;
    if (records.length >= 2) {
      const current = Number(records[records.length - 1].total) || 0;
      const prev = Number(records[records.length - 2].total) || 0;
      if (prev > 0) {
        momActiveBiz = ((current - prev) / prev) * 100;
      } else if (current > 0) {
        momActiveBiz = 100;
      }
    } else if (records.length === 1) {
      const current = Number(records[0].total) || 0;
      if (current > 0) momActiveBiz = 100;
    }

    this.kpis.activeBusinesses = {
      value: activeBizValue,
      meta: `All-time ${this.formatNumber(tbStats.all_time_doctors_count || 0)} · Dental ${this.formatNumber(abStats.dental || 0)} · HC ${this.formatNumber(abStats.healthcare || 0)} · Non-HC ${this.formatNumber(abStats.non_healthcare || 0)}`,
      mom: momActiveBiz
    };

    const countryData = this.getCountryContractsData(res.countryContracts)
      .filter((c: any) => Number(c.total) > 0);
    const allTimeContracts = rcStats.total_overall_contracts || rcStats.total_contracts_overall || getRecordsStats(res.rangeContractsOverall).total_contracts || 0;
    const momContracts = includeComparison && rcStatsPrev.total_contracts
      ? ((totalContracts - rcStatsPrev.total_contracts) / rcStatsPrev.total_contracts) * 100
      : 0;

    // Contract Type Mix Chart Data
    const ctData = this.getContractTypesOverall(res.contractTypes)
      .filter((r: any) => (Number(r.count) || 0) > 0);
    const ctTotal = ctData.reduce((sum: number, r: any) => sum + (Number(r.count) || 0), 0);
    const ctSorted = [...ctData].sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0));

    let topContractsMeta = '';
    if (ctTotal > 0 && ctSorted.length > 0) {
      const top1 = ctSorted[0];
      const percentage = Math.round((Number(top1.count) || 0) / ctTotal * 100);
      const name = this.getContractTypeLabel(top1);
      topContractsMeta = ` · ${percentage}% are ${name}`;
    }
    this.kpis.totalContracts = {
      value: totalContracts,
      meta: `All-time ${this.formatNumber(allTimeContracts)} ${topContractsMeta}`,
      mom: momContracts
    };

    const bizDataPrev = includeComparison ? (res.bizContractsPrev?.data || {}) : {};
    const momActivation = includeComparison && bizDataPrev.activation_rate_pct
      ? ((bizData.activation_rate_pct - bizDataPrev.activation_rate_pct) / bizDataPrev.activation_rate_pct) * 100
      : 0;
    this.kpis.activationRate = {
      value: bizData.activation_rate_pct || 0,
      meta: `${this.formatNumber(bizData.businesses_with_contracts || 0)} of ${this.formatNumber(bizData.total_businesses || 0)} enrolled wrote a contract · ${this.formatNumber(Math.max(0, (bizData.total_businesses || 0) - (bizData.businesses_with_contracts || 0)))} have not`,
      mom: momActivation
    };

    const totalContractsSum = mergedSubInd.reduce((sum: number, row: any) => sum + (Number(row.total_contracts) || 0), 0);
    const topSpecialty = mergedSubInd.reduce((max: any, row: any) => (Number(row.total_contracts) > (Number(max?.total_contracts) || 0)) ? row : max, null);
    const topShare = totalContractsSum && topSpecialty ? (topSpecialty.total_contracts / totalContractsSum) * 100 : 0;

    const totalContractsSumPrev = includeComparison
      ? mergedSubIndPrev.reduce((sum: number, row: any) => sum + (Number(row.total_contracts) || 0), 0)
      : 0;
    const topSpecialtyPrev = includeComparison
      ? mergedSubIndPrev.reduce((max: any, row: any) => (Number(row.total_contracts) > (Number(max?.total_contracts) || 0)) ? row : max, null)
      : null;
    const topSharePrev = includeComparison && totalContractsSumPrev && topSpecialtyPrev
      ? (topSpecialtyPrev.total_contracts / totalContractsSumPrev) * 100
      : 0;
    const momTopShare = includeComparison && topSharePrev
      ? ((topShare - topSharePrev) / topSharePrev) * 100
      : 0;
    this.kpis.topIndustryShare = {
      value: topShare,
      meta: `${topSpecialty?.speciality_name || 'N/A'} · ${this.formatNumber(topSpecialty?.total_contracts || 0)} of ${this.formatNumber(totalContractsSum)} contracts`,
      mom: momTopShare
    };

    // Top Markets — country_contracts (mapping doc)
    const sortedCountries = [...countryData].sort(
      (a, b) => Number(b.total) - Number(a.total)
    );
    const totalCountryContracts = sortedCountries.reduce(
      (sum, c) => sum + Number(c.total || 0),
      0
    );
    const top4 = sortedCountries.slice(0, 4);
    const top4Total = top4.reduce((sum, c) => sum + Number(c.total || 0), 0);
    const top4SharePct = totalCountryContracts
      ? Math.round((top4Total / totalCountryContracts) * 100)
      : 0;

    // Ranks 5+ per mapping; always show ranks 5+ as tail (top 4 are in the horizontal bar chart)
    const tailCountries = sortedCountries.slice(4);

    const tailTotal = tailCountries.reduce((sum, c) => sum + Number(c.total || 0), 0);
    const tailSharePct = totalCountryContracts
      ? Math.round((tailTotal / totalCountryContracts) * 100)
      : 0;
    const marketBarColors = ['#8b5cf6', '#a855f7', '#c084fc', '#7c3aed'];

    const chartSubtitleParts = top4.map(c => {
      const code = c.country_code || c.country;
      const share = totalCountryContracts
        ? Math.round((Number(c.total) / totalCountryContracts) * 100)
        : 0;
      return `${code} ${share}%`;
    });

    const maxTotal = top4.length ? Math.max(...top4.map(c => Number(c.total) || 0)) : 0;
    let computedMaxVal = 50;
    if (maxTotal > 0) {
      if (maxTotal <= 10) computedMaxVal = Math.ceil(maxTotal) || 1;
      else if (maxTotal <= 100) computedMaxVal = Math.ceil(maxTotal / 10) * 10;
      else if (maxTotal <= 1000) computedMaxVal = Math.ceil(maxTotal / 100) * 100;
      else computedMaxVal = Math.ceil(maxTotal / 500) * 500;
    }

    this.topMarkets.chartData = top4.map((c, i) => {
      const val = Number(c.total) || 0;
      const barColor = marketBarColors[i] || '#6366f1';
      const share = totalCountryContracts ? (val / totalCountryContracts) * 100 : 0;
      return {
        label: c.country || c.country_code,
        tooltipLabel: c.country || c.country_code,
        value: share,
        share: share,
        displayValue: `${Number.isInteger(share) ? share : share.toFixed(1)}%`,
        color: barColor,
        tooltipTotalValue: this.formatNumber(val),
        segments: [
          { value: share, color: barColor, label: c.country || c.country_code },
          { value: Math.max(0, 100 - share), color: 'rgba(148, 163, 184, 0.2)', label: '<b class="hide-rem-row"></b><style>.horizontal-tooltip div:has(> div > span > .hide-rem-row) { display: none !important; }</style>' }
        ]
      };
    });

    const namedEmerging = tailCountries.slice(0, 3);
    const bucketEmerging = tailCountries.slice(3);
    const bucketTotal = bucketEmerging.reduce((sum, c) => sum + Number(c.total || 0), 0);
    const emergingPctColors = ['#2563eb', '#6366f1', '#d97706'];

    const formatMarketShare = (total: number) =>
      totalCountryContracts ? Math.round((Number(total) / totalCountryContracts) * 1000) / 10 : 0;

    const formatMarketLabel = (c: any) => {
      const code = (c.country_code || '').toString().trim();
      const name = (c.country || '').toString().trim();
      if (code && name && !name.toUpperCase().startsWith(code.toUpperCase())) {
        return `${code} ${name}`;
      }
      return name || code;
    };

    const emergingRows = namedEmerging.map((c, i) => ({
      name: formatMarketLabel(c),
      subtitle: '',
      share: formatMarketShare(c.total),
      total: Number(c.total) || 0,
      pctColor: emergingPctColors[i] || '#6366f1'
    }));

    if (bucketEmerging.length) {
      emergingRows.push({
        name: bucketEmerging.map(c => c.country_code || c.country).filter(Boolean).join(' '),
        subtitle: `${bucketEmerging.length} nascent market${bucketEmerging.length === 1 ? '' : 's'} · seed-stage volume`,
        share: formatMarketShare(bucketTotal),
        total: bucketTotal,
        pctColor: '#d97706'
      });
    }

    this.topMarkets.chartSubtitle = chartSubtitleParts.length
      ? `${chartSubtitleParts.join(' · ')} — together ${top4SharePct}% of contracts`
      : '';

    this.topMarkets.chartFootnote = tailCountries.length
      ? `+ ${tailCountries.length} emerging markets · ${tailSharePct}% · ${this.formatNumber(tailTotal)} contracts — see the next card`
      : '';

    this.topMarkets.emerging = {
      marketCount: tailCountries.length,
      share: tailSharePct,
      rows: emergingRows
    };

    // Driving Numbers
    const bestInd = mergedSubInd.reduce((best: any, row: any) => {
      if (row.total_contracts < 10) return best;
      if (!best) return row;
      const score = (row.total_contracts * row.average_financed_amount) * (100 - row.overdue_ratio);
      const bestScore = (best.total_contracts * best.average_financed_amount) * (100 - best.overdue_ratio);
      return score > bestScore ? row : best;
    }, null) || mergedSubInd[0];

    const getHighestRiskIndustry = (data: any[]) => {
      if (!data || data.length === 0) return null;
      const worst = data.reduce((currentWorst: any, row: any) => {
        if (Number(row.total_contracts) < 10) return currentWorst;
        if (!currentWorst) return row;
        return Number(row.overdue_ratio) > Number(currentWorst.overdue_ratio) ? row : currentWorst;
      }, null);
      return worst ?? data[0];
    };

    const highestOverdueIndustry = getHighestRiskIndustry(mergedSubInd);

    this.drivingNumbers.bestIndustry = bestInd;
    this.drivingNumbers.worstIndustry = highestOverdueIndustry;

    // Contract Type Mix Chart — API: visionboard/tools/contract_types_count (Data parsed above)
    const mixColors = ['#9333ea', '#3b82f6', '#0d9488', '#ea580c', '#16a34a', '#dc2626', '#ec4899', '#f97316'];

    this.contractTypeMixChart = ctSorted.length ? ctSorted.map((c, i) => {
      let fullLabel = this.getContractTypeLabel(c).replace(/EZ Deferred/gi, 'SimpleeFi');
      let label = fullLabel.replace(/\s?Payment/gi, '');
      label = label.replace(/\s?Deferred/gi, '');
      const words = label.split(' ');
      if (words.length > 2) {
        label = words.slice(0, 2).join(' ') + '...';
      }

      return {
        label,
        tooltipLabel: fullLabel,
        value: ctTotal ? ((Number(c.count) || 0) / ctTotal) * 100 : 0,
        color: mixColors[i % mixColors.length]
      };
    }).sort((a: any, b: any) => b.value - a.value) : null;

    if (ctSorted.length >= 2 && ctTotal > 0) {
      const top1 = ctSorted[0];
      const top2 = ctSorted[1];
      const top1Label = this.getContractTypeLabel(top1);
      const top2Label = this.getContractTypeLabel(top2);
      const top1Pct = Math.round(((Number(top1.count) || 0) / ctTotal) * 100);
      const top2Pct = Math.round(((Number(top2.count) || 0) / ctTotal) * 100);
      const combinedPct = top1Pct + top2Pct;
      this.drivingNumbers.contractMixSubtitle = `${top1Label} ${top1Pct}% + ${top2Label} ${top2Pct}% drive ${combinedPct}% of plans`;
    } else {
      this.drivingNumbers.contractMixSubtitle = '';
    }

    // Active Biz Chart
    const abStatsData = res.activeBizStats?.data?.unit_data || [];
    const aggregated = new Map<string, any>();

    abStatsData.forEach((d: any) => {
      const dateStr = d.start || d.start_time || d.period || d.unit_date || d.date;
      const date = dayjs(dateStr);
      const isMultiYear = this.start_date && this.end_date && dayjs(this.start_date).year() !== dayjs(this.end_date).year();
      let format = isMultiYear ? 'MMM DD, YYYY' : 'MMM DD';
      if (this.unit() === 'year') {
        format = 'YYYY';
      } else if (this.unit() === 'month') {
        format = 'MMM YYYY';
      } else if (this.unit() === 'hour') {
        format = 'MMM DD, h A';
      }

      let label = date.format(format);
      if (this.unit() === 'week') {
        const endStr = d.end || d.end_time;
        const endDate = endStr ? dayjs(endStr) : date.add(6, 'day');
        label = isMultiYear ? `${date.format('DD MMM')} - ${endDate.format('DD MMM YYYY')}` : `${date.format('DD MMM')} - ${endDate.format('DD MMM')}`;
      } else if (this.unit() === 'quarter') {
        const qNum = Math.floor(date.month() / 3) + 1;
        label = `Q${qNum} ${date.format('YYYY')}`;
      }

      if (!aggregated.has(label)) {
        aggregated.set(label, { label, dental: 0, healthcare: 0, non_healthcare: 0, timestamp: date.valueOf() });
      }
      const item = aggregated.get(label)!;
      item.dental += (Number(d.dental) || 0);
      item.healthcare += (Number(d.healthcare) || 0);
      item.non_healthcare += (Number(d.non_healthcare) || 0);
    });

    const abChartData = Array.from(aggregated.values()).sort((a, b) => a.timestamp - b.timestamp);

    this.activeBizChart = {
      data: abChartData,
      hasData: abChartData.some((d: any) => d.dental > 0 || d.healthcare > 0 || d.non_healthcare > 0),
      showOnlyFirstLastXAxis: true,
      legend: [
        { key: 'non_healthcare', label: 'Non-Healthcare', color: '#9333ea' },
        { key: 'healthcare', label: 'Healthcare', color: '#3b82f6' },
        { key: 'dental', label: 'Dental', color: '#0d9488' }
      ]
    };

    // Waterfall Chart
    const inflows = [
      { label: 'Upfront', value: getStats(res.coll[0]).amount || 0, color: '#9333ea' },
      { label: 'Downpayment', value: getStats(res.coll[1]).amount || 0, color: '#0d9488' },
      { label: 'Late Fee', value: getStats(res.coll[3]).amount || 0, color: '#ea580c' },
      { label: 'Enrollment', value: getStats(res.coll[2]).amount || 0, color: '#6366f1' }
    ];

    const outflows = [
      { label: 'Refunds', value: -(getStats(res.leak?.[0]).amount || 0), color: '#dc2626' },
      { label: 'Reversals', value: -(getStats(res.leak?.[1]).amount || 0), color: '#f97316' }
    ];

    // Sort by value descending as requested
    const waterfallData = [...inflows, ...outflows].sort((a, b) => b.value - a.value);
    this.waterfallChart = {
      data: waterfallData,
      hasData: waterfallData.some(d => Math.abs(d.value) > 0),
      net: netRevenue / 1000000, // For the $1.81M formatting
      leakage: leakage
    };
  }

  getDelinquentCount(row: any): number {
    if (!row) return 0;
    return Math.round((row.total_contracts * row.overdue_ratio) / 100);
  }

  // CRITICAL BUSINESS DATA FOR CHURN RATE CARD AND RISK MODAL
  private fetchCriticalBusinessData() {
    this.isCriticalBusinessLoading.set(true);
    const payload = this.buildPayload({
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit(),
      overall: 0
    });
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };

    this.apiService.post('visionboard/tools/get_critical_business', payload, options).subscribe({
      next: (res: any) => {
        if (res && res.result) {
          const currentResult = res.result;

          // MoM Calculation key
          const currentChurn = Number(currentResult.portfolio_churn_rate) || 0;
          const prevChurn = Number(currentResult.previous_portfolio_churn_rate) || 0;

          let churnGrowth = 0;
          if (prevChurn !== 0) {
            churnGrowth = ((currentChurn - prevChurn) / prevChurn) * 100;
          } else if (currentChurn !== 0) {
            churnGrowth = 100;
          }

          const growthPrefix = churnGrowth > 0 ? '+' : '';
          currentResult.churnGrowthText = `${growthPrefix}${churnGrowth.toFixed(1)}%`;

          this.criticalBusinessData.set(currentResult);
        }
        this.isCriticalBusinessLoading.set(false);
      },
      error: () => {
        this.isCriticalBusinessLoading.set(false);
      }
    });
  }

  openRiskModal() {
    this.riskSearchQuery.set('');
    this.selectedRiskTier.set('All');
    this.riskCurrentPage.set(1);
    this.isRiskModalOpen.set(true);
  }

  closeRiskModal() {
    this.isRiskModalOpen.set(false);
  }

  setRiskTier(tier: string) {
    this.selectedRiskTier.set(tier);
    this.riskCurrentPage.set(1);
  }

  setRiskSearchQuery(query: string) {
    this.riskSearchQuery.set(query);
    this.riskCurrentPage.set(1);
  }

  prevRiskPage() {
    if (this.riskCurrentPage() > 1) {
      this.riskCurrentPage.update(p => p - 1);
    }
  }

  nextRiskPage() {
    if (this.riskCurrentPage() < this.riskTotalPages()) {
      this.riskCurrentPage.update(p => p + 1);
    }
  }

  onRiskPageChange(page: number) {
    this.riskCurrentPage.set(page);
  }

  onRiskPageSizeChange(size: number) {
    this.riskPageSize.set(size);
    this.riskCurrentPage.set(1);
  }
}
